/*
  Photo carousel for the one story layout of sections/cm-raw-reviews.liquid (Testimonials: Kaia's photos).
  Without this script the photos are a strip to swipe through and the buttons stay hidden. With it:
  - the photos rotate by themselves every few seconds ("Rotate the photos by themselves"), only while the carousel is
    on screen, the tab is visible, and nobody is hovering over it or focused inside it;
  - Pause stops them and Play starts them again (moving content needs a way to stop it); using previous, next or a
    swipe stops them for good, so people read at their own pace;
  - with reduced motion they never start, and moving between photos doesn't animate;
  - the count shows the photo on screen, and screen readers hear each photo as "2 of 3" when the rotation is paused.
  Videos (a "Carousel video" block) take their turn in the strip:
  - the still shows first; the file is only requested when the video's turn comes;
  - while the photos move, the video plays through once with no sound, fades in only once it is really playing, and
    the next photo comes when it ends; hovering or focusing lets it finish but holds the next photo back;
  - when the photos are stopped (Pause, a button, a swipe), with reduced motion or data saver, or if the phone blocks
    autoplay, the video stays still and shows its own play button, so visitors start it themselves;
  - a video left alone on screen (the only slide) loops, with its own controls to stop it.
  Re-initialises when the theme editor reloads the section, and shows a photo when its block is selected there.
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const saveData = () => Boolean(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));

  function init(root) {
    if (!root || root.dataset.cmCarouselReady) return;
    const track = root.querySelector('[data-cm-carousel-track]');
    const slides = track ? [...track.children] : [];
    const videoOf = (slide) => (slide ? slide.querySelector('video[data-src]') : null);
    if (slides.length < 2 && !slides.some(videoOf)) return;
    root.dataset.cmCarouselReady = 'true';

    const pause = root.querySelector('[data-cm-carousel-pause]');
    const pauseText = root.querySelector('[data-cm-carousel-pause-text]');
    const prev = root.querySelector('[data-cm-carousel-prev]');
    const next = root.querySelector('[data-cm-carousel-next]');
    const count = root.querySelector('[data-cm-carousel-count]');
    const seconds = Math.min(10, Math.max(4, Number(root.dataset.interval) || 6));

    let index = 0;
    let playing = root.hasAttribute('data-autoplay') && !reducedMotion.matches;
    let hovered = false;
    let focused = false;
    let onScreen = false;
    let timer = null;
    let programmatic = false;
    let scrollEnd = null;
    let videoBlocked = false; // autoplay refused or the file failed: videos wait for a tap, the photos move on

    [pause, prev, next].forEach((button) => button && (button.hidden = false));
    root.classList.add('is-ready');

    const setPauseButton = () => {
      if (!pause) return;
      const label = playing ? pause.dataset.pauseLabel : pause.dataset.playLabel;
      if (pauseText) pauseText.textContent = label;
      pause.classList.toggle('is-paused', !playing);
      track.setAttribute('aria-live', playing ? 'off' : 'polite');
    };

    const visible = () => onScreen && document.visibilityState === 'visible';
    const moving = () => playing && visible() && !hovered && !focused;
    const alone = slides.length === 1;
    const autoVideo = () => !reducedMotion.matches && !saveData() && !videoBlocked;

    const attach = (video) => {
      if (!video.getAttribute('src')) video.src = video.dataset.src;
      video.muted = true; // iOS wants the property as well as the attribute before play()
      video.hidden = false;
    };
    // The visitor's own way in: the still as its poster, and the browser's play button.
    const offerControls = (video) => {
      if (video.dataset.poster && !video.poster) video.poster = video.dataset.poster;
      attach(video);
      video.controls = true;
    };

    // Plays the video on screen while things move; pauses every other one.
    const syncVideos = () => {
      slides.forEach((slide, i) => {
        const video = videoOf(slide);
        if (!video) return;
        if (i !== index) {
          if (!video.paused) video.pause();
          return;
        }
        if ((playing || alone) && visible() && autoVideo()) {
          attach(video);
          video.controls = alone;
          video.loop = alone;
          const attempt = video.play();
          if (attempt) {
            attempt.catch(() => {
              videoBlocked = true;
              offerControls(video);
              schedule();
            });
          }
        } else {
          // Started by the carousel: stop it. Started by the visitor (controls showing): theirs to stop.
          if (!video.controls && !video.paused) video.pause();
          offerControls(video);
        }
      });
    };

    const go = (to, animate = true) => {
      index = (to + slides.length) % slides.length;
      programmatic = true;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: animate && !reducedMotion.matches ? 'smooth' : 'auto' });
      if (count) count.textContent = `${index + 1} / ${slides.length}`;
      window.clearTimeout(scrollEnd);
      scrollEnd = window.setTimeout(() => { programmatic = false; }, 700);
      syncVideos();
    };

    function schedule() {
      window.clearTimeout(timer);
      timer = null;
      syncVideos();
      if (alone || !moving()) return;
      // A playing video moves things on itself, when it ends.
      if (videoOf(slides[index]) && autoVideo()) return;
      timer = window.setTimeout(() => {
        go(index + 1);
        schedule();
      }, seconds * 1000);
    }

    slides.forEach((slide) => {
      const video = videoOf(slide);
      if (!video) return;
      video.addEventListener('playing', () => video.classList.add('is-live'));
      video.addEventListener('ended', () => {
        if (!video.controls && moving()) {
          go(index + 1);
          schedule();
        }
      });
      // A file that won't play: drop it, so the slide is just its still from now on.
      video.addEventListener('error', () => {
        video.removeAttribute('data-src');
        video.removeAttribute('src');
        video.hidden = true;
        schedule();
      });
    });
    const stop = () => {
      playing = false;
      setPauseButton();
      schedule();
    };

    pause?.addEventListener('click', () => {
      playing = !playing;
      if (playing && reducedMotion.matches) playing = false;
      setPauseButton();
      schedule();
    });
    prev?.addEventListener('click', () => { stop(); go(index - 1); });
    next?.addEventListener('click', () => { stop(); go(index + 1); });

    // A swipe or a scroll of the strip is the visitor's own move: follow it, and stop rotating.
    track.addEventListener('scroll', () => {
      if (programmatic) return;
      window.clearTimeout(scrollEnd);
      scrollEnd = window.setTimeout(() => {
        const nearest = slides.reduce((best, slide, i) => (Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - track.scrollLeft) ? i : best), 0);
        if (nearest !== index) {
          index = nearest;
          if (count) count.textContent = `${index + 1} / ${slides.length}`;
          stop();
        }
      }, 120);
    }, { passive: true });

    root.addEventListener('mouseenter', () => { hovered = true; schedule(); });
    root.addEventListener('mouseleave', () => { hovered = false; schedule(); });
    root.addEventListener('focusin', () => { focused = true; schedule(); });
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) { focused = false; schedule(); }
    });
    document.addEventListener('visibilitychange', schedule);
    reducedMotion.addEventListener?.('change', () => { if (reducedMotion.matches) stop(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        onScreen = entries.some((entry) => entry.isIntersecting);
        schedule();
      }, { threshold: 0.4 }).observe(root);
    } else {
      onScreen = true;
    }

    root.cmCarouselShow = (slide) => {
      const i = slides.indexOf(slide);
      if (i >= 0) { stop(); go(i, false); }
    };

    setPauseButton();
    schedule();
  }

  const initAll = (scope = document) => scope.querySelectorAll('[data-cm-carousel]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('.cm-raw-reviews__slide');
    const root = slide && slide.closest('[data-cm-carousel]');
    if (root && root.cmCarouselShow) root.cmCarouselShow(slide);
  });
})();
